import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ExternalLink, Gauge } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { m } from "@/paraglide/messages.js";
import {
	buildSystemStatus,
	serviceStatusQueryOptions,
	type SystemNotice,
	type SystemSeverity,
	type SystemStatusView,
	useStatusPageUrl,
} from "@/services/status-service.ts";
import { statusBannerQueryOptions } from "@/services/status-banner-service.ts";
import { fmtDateTime } from "@/shared/format.ts";
import { cn } from "@/utils/cn.ts";
import { localized } from "@/utils/localized.ts";
import { Z_LAYERS } from "@/styles/z-layers.ts";

/**
 * Single customer-facing status strip. Merges the super-admin manual notice
 * and the oneDeploy incident feed (plus live health) into one bar coloured by
 * the worst active severity; the full list — however many incidents — lives in
 * an expandable panel rather than a stack of bars.
 *
 * Rendered in-flow (so it pushes content down instead of overlaying it) in both
 * the authenticated app shell and the pre-login auth layout. Both sources read
 * without a token — the banner endpoint is public and the oneDeploy feed is
 * same-origin — so the same component serves either layout unchanged.
 */
export function SystemStatusBanner() {
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const { data: serviceStatus } = useQuery(serviceStatusQueryOptions());
	const { data: adminBanner } = useQuery(statusBannerQueryOptions());

	useEffect(() => {
		if (!open) {
			return;
		}
		const onPointerDown = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("pointerdown", onPointerDown);
		return () => document.removeEventListener("pointerdown", onPointerDown);
	}, [open]);

	const view = buildSystemStatus({
		admin: adminBanner
			? {
					enabled: adminBanner.enabled,
					message:
						localized({
							en: adminBanner.messageEn,
							de: adminBanner.messageDe,
						}) || m.status_banner_default_message(),
					severity: adminBanner.severity,
					activatedAt: adminBanner.activatedAt,
				}
			: null,
		status: serviceStatus,
	});

	if (!view.severity) {
		return null;
	}

	const isSevere = view.severity === "critical";
	// "Service notice · Live since 14:32" — the state's own name, plus when it
	// began. The timestamp is included ONLY while a single notice is on the bar:
	// with several, one bare time would silently be read as applying to all of
	// them. Their individual times are in the panel, one per row.
	const subLine = [
		statusLabel(view),
		view.notices.length === 1 ? noticeTime(view.notices[0]) : undefined,
	]
		.filter((part) => part !== undefined)
		.join(" · ");

	return (
		<div ref={containerRef} className="relative">
			<button
				type="button"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				className={cn(
					"w-full flex items-center gap-3 px-4 sm:px-6 py-2.5 text-left shadow-sm cursor-pointer transition-opacity",
					isSevere
						? "bg-destructive text-destructive-foreground"
						: "bg-warning text-warning-foreground",
				)}
			>
				<AlertTriangle className="w-4 h-4 shrink-0" />
				<span className="flex-1 min-w-0">
					<span className="block text-xs sm:text-sm font-medium truncate">
						{summaryLine(view)}
					</span>
					{/*
					 * Facts only. This line used to carry a fixed reassurance chosen by
					 * severity alone ("you can keep working…" / "this isn't something you
					 * did…") — a claim nobody had checked, asserted over every outage and
					 * every announcement alike. It now states what we actually know: what
					 * this state is called, and since when. Anything reassuring belongs
					 * in the admin's own message, where a person is accountable for it.
					 */}
					<span className="block text-[11px] sm:text-xs opacity-80 truncate">
						{subLine}
					</span>
				</span>
				{view.notices.length > 1 && (
					<span
						className={cn(
							"shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold border",
							isSevere
								? "bg-white/20 border-white/30"
								: "bg-black/10 border-black/20",
						)}
					>
						{m.system_status_count_active({ count: view.notices.length })}
					</span>
				)}
				<span
					className={cn(
						"shrink-0 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold border",
						isSevere
							? "bg-white/15 border-white/25"
							: "bg-black/8 border-black/15",
					)}
				>
					{open ? m.system_status_hide() : m.system_status_details()}
					<ChevronDown
						className={cn(
							"w-3.5 h-3.5 transition-transform",
							open && "rotate-180",
						)}
					/>
				</span>
			</button>

			{open && <StatusPanel view={view} />}
		</div>
	);
}

function summaryLine(view: SystemStatusView): string {
	const { notices } = view;
	if (notices.length === 0) {
		return view.severity === "critical"
			? m.system_status_summary_unavailable()
			: m.system_status_summary_slow();
	}
	if (notices.length === 1) {
		return notices[0]?.title ?? "";
	}
	const crit = notices.filter((n) => n.severity === "critical").length;
	const warn = notices.filter((n) => n.severity === "warning").length;
	const parts: string[] = [];
	if (crit) {
		parts.push(m.system_status_count_critical({ count: crit }));
	}
	if (warn) {
		parts.push(m.system_status_count_warning({ count: warn }));
	}
	return m.system_status_summary_multiple({ breakdown: parts.join(", ") });
}

/**
 * What to call the current state. Shared by the bar's second line and the panel
 * header so the two can never disagree about the same severity.
 */
function statusLabel(view: SystemStatusView): string {
	if (view.severity === "critical") {
		return m.system_status_disruption_label();
	}
	return view.notices.length === 0
		? m.system_status_degraded_label()
		: m.system_status_notice_label();
}

/**
 * When a notice began, captioned as its source warrants: an admin notice was
 * switched on ("live since …"), an incident carries its own start time. One
 * rule, used by the bar's second line and by every row of the panel.
 */
function noticeTime(notice: SystemNotice | undefined): string | undefined {
	if (!notice?.time) {
		return undefined;
	}
	return notice.source === "admin"
		? m.status_banner_live_since({ time: fmtDateTime(notice.time) })
		: fmtDateTime(notice.time);
}

function StatusPanel({ view }: { view: SystemStatusView }) {
	const headTitle = statusLabel(view);
	const statusPageUrl = useStatusPageUrl();

	const stateWord =
		view.health.status === "operational"
			? m.system_status_state_operational()
			: view.health.status === "degraded"
				? m.system_status_state_degraded()
				: view.health.status === "down"
					? m.system_status_state_down()
					: m.system_status_state_unknown();

	return (
		<div
			className="absolute top-full left-3 right-3 mt-2 max-w-lg rounded-xl border bg-popover text-popover-foreground shadow-lg overflow-hidden"
			style={{ zIndex: Z_LAYERS.popover }}
		>
			<div className="px-4 py-3 border-b">
				<div className="flex items-center gap-2 text-sm font-semibold">
					<SeverityDot severity={view.severity} />
					{headTitle}
				</div>
			</div>

			<div className="max-h-64 overflow-y-auto">
				{view.notices.length === 0 ? (
					<p className="px-4 py-5 text-center text-xs text-muted-foreground">
						{m.system_status_no_incidents()}
					</p>
				) : (
					view.notices.map((notice) => (
						<NoticeRow key={notice.id} notice={notice} />
					))
				)}
			</div>

			<div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-muted/40 border-t text-xs text-muted-foreground">
				<span className="flex items-center gap-1.5">
					<Gauge className="w-3.5 h-3.5" />
					{stateWord}
					{view.health.uptime90d !== undefined && (
						<>
							{" · "}
							<span className="tabular-nums font-medium text-foreground">
								{m.system_status_uptime({ uptime: view.health.uptime90d })}
							</span>
						</>
					)}
				</span>
				<a
					href={statusPageUrl}
					target="_blank"
					rel="noreferrer"
					className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
				>
					{m.system_status_view_full()}
					<ExternalLink className="w-3 h-3" />
				</a>
			</div>
		</div>
	);
}

function NoticeRow({ notice }: { notice: SystemNotice }) {
	const isAdmin = notice.source === "admin";
	const time = noticeTime(notice);
	return (
		<div className="flex gap-3 px-4 py-3 border-b last:border-b-0">
			<span
				className={cn(
					"w-0.5 shrink-0 rounded self-stretch",
					notice.severity === "critical" ? "bg-destructive" : "bg-warning",
				)}
			/>
			<div className="min-w-0 flex-1">
				{/*
				 * `items-start` so the severity tag stays on the first line of a notice
				 * that wraps onto several.
				 */}
				<div className="flex items-start gap-2 flex-wrap">
					<span
						className={cn(
							"rounded px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wide",
							isAdmin
								? "bg-primary/10 text-primary"
								: notice.severity === "critical"
									? "bg-destructive/15 text-destructive"
									: "bg-warning/20 text-warning-foreground dark:text-warning",
						)}
					>
						{isAdmin
							? m.system_status_admin_tag()
							: notice.severity === "critical"
								? m.system_status_sev_critical()
								: m.system_status_sev_warning()}
					</span>
					{/*
					 * Wraps in full — no truncation. The bar is a one-line strip and
					 * clips by design, so THIS is the only place a long notice can be
					 * read; truncating here left no surface that showed the whole
					 * message anywhere. `wrap-break-word` covers an unbroken paste (a
					 * URL), and the list above scrolls once the panel is full.
					 */}
					<span className="min-w-0 flex-1 text-sm font-medium wrap-break-word">
						{notice.title}
					</span>
				</div>
				{time !== undefined && (
					<div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
						{time}
					</div>
				)}
				{notice.detail && (
					<p className="mt-1 pl-2.5 border-l-2 text-xs text-muted-foreground">
						{notice.detail}
					</p>
				)}
			</div>
		</div>
	);
}

function SeverityDot({ severity }: { severity: SystemSeverity | null }) {
	return (
		<span
			className={cn(
				"w-2 h-2 rounded-full shrink-0",
				severity === "critical" ? "bg-destructive" : "bg-warning",
			)}
		/>
	);
}
